import { computed } from 'vue';

/**
 * useAdjacencyRules
 *
 * Optional composable that enforces seat adjacency constraints.
 * To be used as a plugin/strategy injected into `useSeatSelection` via the
 * `adjacencyRules` option when the consumer needs `forzar_adyacentes` behaviour.
 *
 * Rules enforced:
 *   1. All selected seats must be in the same row.
 *   2. Selected seat numbers must form a contiguous block
 *      (no gaps when sorted numerically).
 *   3. The candidate seat to add must be adjacent to the current block
 *      (i.e. number === min-1 or number === max+1), OR the selection is empty.
 *
 * Deselection is always allowed: removing a seat from the middle of a block
 * would break contiguity, so we validate the *resulting* set after removal.
 *
 * Design decision: implemented as a standalone composable rather than inlining
 * inside `useSeatSelection`. This keeps the base composable simple (no adjacency
 * overhead for flows that do not need it) and follows the Strategy pattern —
 * the consumer chooses the rule set it wants to apply.
 *
 * @param {Object}      options
 * @param {Ref<Set>}    options.selectedIds  - The reactive Set of selected seat ids
 *                                            (internal ref from useSeatSelection, passed by reference).
 * @param {Ref<Array>}  options.seats        - Reactive array of all seat objects.
 * @returns {Object} Public API — see bottom of file.
 */
export function useAdjacencyRules({ selectedIds, seats }) {
    // ─── Helpers ─────────────────────────────────────────────────────────────

    /**
     * Returns the seat objects that correspond to a given Set of ids.
     * @param {Set<string>} ids
     * @returns {Array}
     */
    function _getSeatsForIds(ids) {
        const list = seats.value ?? seats;
        return [...ids]
            .map(id => list.find(s => String(s.id) === id))
            .filter(Boolean);
    }

    /**
     * Given a set of seat objects, return the set of unique row values.
     * @param {Array} seatObjects
     * @returns {Set<string>}
     */
    function _rows(seatObjects) {
        return new Set(seatObjects.map(s => String(s.row)));
    }

    /**
     * Given a set of seat objects (same row assumed), returns true if
     * their numbers form a contiguous block (no gaps).
     * @param {Array} seatObjects
     * @returns {boolean}
     */
    function _isContiguous(seatObjects) {
        if (seatObjects.length <= 1) return true;
        const nums = seatObjects.map(s => Number(s.number)).sort((a, b) => a - b);
        for (let i = 1; i < nums.length; i++) {
            if (nums[i] !== nums[i - 1] + 1) return false;
        }
        return true;
    }

    // ─── Core validation ─────────────────────────────────────────────────────

    /**
     * Validates whether adding `seat` to the current selection is allowed
     * under adjacency rules.
     *
     * @param {{ id, row, number }} seat  - Seat being added
     * @returns {{ allowed: boolean, reason: string|null }}
     */
    function canAdd(seat) {
        const currentIds = selectedIds.value;

        // Empty selection — first seat is always ok
        if (currentIds.size === 0) {
            return { allowed: true, reason: null };
        }

        const currentSeats = _getSeatsForIds(currentIds);
        const rows = _rows(currentSeats);

        // All selected seats must share the same row
        if (!rows.has(String(seat.row))) {
            return { allowed: false, reason: 'different_row' };
        }

        // The candidate must extend the contiguous block (adjacent to min-1 or max+1)
        const nums = currentSeats.map(s => Number(s.number)).sort((a, b) => a - b);
        const min = nums[0];
        const max = nums[nums.length - 1];
        const n = Number(seat.number);

        if (n !== min - 1 && n !== max + 1) {
            return { allowed: false, reason: 'not_adjacent' };
        }

        return { allowed: true, reason: null };
    }

    /**
     * Validates whether removing `seat` from the current selection leaves a
     * valid contiguous block (or an empty selection).
     *
     * Removing a seat from the middle of a block would leave a gap — forbidden.
     *
     * @param {{ id, row, number }} seat  - Seat being removed
     * @returns {{ allowed: boolean, reason: string|null }}
     */
    function canRemove(seat) {
        const currentIds = selectedIds.value;
        if (!currentIds.has(String(seat.id))) {
            // Not selected — nothing to validate
            return { allowed: true, reason: null };
        }

        // Simulate the resulting set after removal
        const resultIds = new Set(currentIds);
        resultIds.delete(String(seat.id));

        if (resultIds.size === 0) {
            // Clearing last seat is always ok
            return { allowed: true, reason: null };
        }

        const resultSeats = _getSeatsForIds(resultIds);
        if (!_isContiguous(resultSeats)) {
            return { allowed: false, reason: 'would_break_contiguity' };
        }

        return { allowed: true, reason: null };
    }

    /**
     * High-level predicate used by useSeatSelection to decide if a toggle is
     * allowed under adjacency rules.
     *
     * @param {{ id, row, number, available }} seat
     * @param {boolean} isCurrentlySelected
     * @returns {{ allowed: boolean, reason: string|null }}
     */
    function validateToggle(seat, isCurrentlySelected) {
        if (isCurrentlySelected) {
            return canRemove(seat);
        }
        return canAdd(seat);
    }

    // ─── Exposed error state ─────────────────────────────────────────────────

    /**
     * A human-readable error key for the last blocked toggle attempt.
     * Consumers can react to this to show UI feedback.
     *
     * Values:
     *   null              — no error / rules not violated
     *   'different_row'   — tried to select a seat from a different row
     *   'not_adjacent'    — tried to select a non-adjacent seat
     *   'would_break_contiguity' — tried to deselect a middle seat in the block
     */
    const lastError = computed(() => null); // managed by useSeatSelection externally

    // ─── Public API ──────────────────────────────────────────────────────────
    return {
        /**
         * Validates a seat toggle (add or remove) against adjacency rules.
         * Returns { allowed: boolean, reason: string|null }.
         */
        validateToggle,
        /** Direct add validation (useful for external consumers) */
        canAdd,
        /** Direct remove validation (useful for external consumers) */
        canRemove,
    };
}
